import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AutoCompleteMultiUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  ModalFilters,
  Table,
  useFetch,
  UsePrivileges,
} from "@4uhub/lib4uhub";

import { Box, Typography } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { ISalesContract } from "../../../models/sales-contract";
import MainApi from "../../../services/mainApi.service";
import SituationChip from "./SituationChip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import CancelIcon from "@mui/icons-material/Cancel";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  approveSalesContract,
  cancelSalesContract,
  reactivateSalesContract,
  rescendSalesContract,
} from "../../../services/salesContract.service";

export const translationPath = "page.4uSalesContract.registers.baseContracts.";

const x = process.env.REACT_APP_MAIN + "/api/v1/salesContract/";
const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);
const service = new MainApi(x);

const AdvantagesAndBenefitsClubPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterParams, setFilterParams] = useState<
    Record<string, any> | undefined
  >();

  const [enableFields, setEnableFields] = useState<boolean>(true);
  const [selectedSituation, setSelectedSituation] = useState<ISelectType[]>([]);
  const [selectedSituationDefault, setSelectedSituationDefault] = useState<
    ISelectType[]
  >([]);
  const [situations, setSituations] = useState<ISelectType[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState<ISelectType | null>(
    null
  );
  const filterOptions: ISelectType[] = useMemo(
    () => [
      {
        id: "contratationDate",
        name: t(translationPath + "contratation_date") || "Contratation Date",
        value: "contratationDate",
      },
      {
        id: "terminationDate",
        name: t(translationPath + "termination_date") || "Termination Date",
        value: "terminationDate",
      },
    ],
    []
  );

  const { sendRequest: approve } = useFetch(approveSalesContract);
  const { sendRequest: cancel } = useFetch(cancelSalesContract);
  const { sendRequest: reactivate } = useFetch(reactivateSalesContract);
  const { sendRequest: rescend } = useFetch(rescendSalesContract);
  const { sendRequest } = useFetch(genericService.list);

  const { privileges } = UsePrivileges();

  const permissionApproveContract = !!privileges.find(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "salescontractapprove"
  );
  const permissionRescendContract = !!privileges.find(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "salescontractrescend"
  );
  const permissionCancelContract = !!privileges.find(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "salescontractcancel"
  );
  const permissionReactivateContract = !!privileges.find(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "salescontractreactivate"
  );

  const deleteContract = !!privileges.find(
    (priv) =>
      priv.domainName === "all" ||
      (priv.domainName === "salescontract" && priv.actions.includes("Delete"))
  );

  const formatDate = (date: string) => {
    if (!date) return "-";
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const fetchSituations = useCallback(async () => {
    const { data, success } = await sendRequest({
      Identifier: "SalesContractsSituation",
    });
    if (data && success) {
      setSituations(data);
      const selectedSituationData = data.find(
        (situation) => situation.code === "1"
      );
      if (selectedSituationData) {
        setSelectedSituation([selectedSituationData]);
        setSelectedSituationDefault([selectedSituationData]);
      }
    }
  }, [sendRequest]);

  const columns: GridColDef<ISalesContract>[] = [
    {
      field: "code",
      headerName: t(translationPath + "code")!,
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "contractTitle",
      headerName: t(translationPath + "name")!,
    },
    {
      field: "contractDate",
      headerName: t(translationPath + "contratation_date")!,
      renderCell: ({ value }) => (
        <Typography variant="body2">{formatDate(value)}</Typography>
      ),
    },
    {
      field: "terminationDate",
      headerName: t(translationPath + "termination_date")!,
      renderCell: ({ value }) => (
        <Typography variant="body2">{formatDate(value)}</Typography>
      ),
    },
    {
      field: "salesContractClassification",
      headerName: t(translationPath + "contract_classification")!,
      renderCell: ({ value }) => (
        <Typography variant="body2">{value?.name ?? "-"}</Typography>
      ),
    },
    {
      field: "integrationId",
      headerName: t(translationPath + "integration_code")!,
      renderCell: ({ value }) => (
        <Typography variant="body2">{value ?? "-"}</Typography>
      ),
    },
    {
      field: "situation",
      headerName: t(translationPath + "situation_status")!,
      renderCell: ({ value }) => <SituationChip code={value?.code} />,
    },
  ];

  const onCancelSalesContract = useCallback(
    async (id: string) => {
      const { success } = await cancel(id);
      if (success) {
        setRefresh((p) => p + 1);
        filterHandler();
        cleanFileterHandler();
      }
    },
    [cancel]
  );

  const onApproveSalesContract = useCallback(
    async (id: string) => {
      const { success } = await approve(id);
      if (success) {
        setRefresh((p) => p + 1);
        filterHandler();
        cleanFileterHandler();
      }
    },
    [approve]
  );

  const onReactivationSalesContract = useCallback(
    async (id: string) => {
      const { success } = await reactivate(id);
      if (success) {
        setRefresh((p) => p + 1);
        filterHandler();
        cleanFileterHandler();
      }
    },
    [reactivate]
  );

  const onRescendSalesContract = useCallback(
    async (id: string) => {
      const { success } = await rescend(id);
      if (success) {
        setRefresh((p) => p + 1);
        filterHandler();
        cleanFileterHandler();
      }
    },
    [rescend]
  );

  const handleRowClick = ({ id }: GridRowParams<any>) => {
    navigate(`${id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  const handleSituationSelected = (value: ISelectType[] | null) => {
    setSelectedSituation(value || []);
  };

  const hasFilter = selectedSituation.length > 0 || (startDate && endDate);

  const filterHandler = useCallback(() => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      setStartDate(null);
      setEndDate(null);
    }
    setFilterParams({
      SituationCodes:
        selectedSituation.length > 0
          ? selectedSituation.map((s) => s.code)
          : undefined,
      InitialDate:
        startDate && endDate
          ? startDate.toISOString().substring(0, 10)
          : undefined,
      EndDate:
        endDate && startDate
          ? endDate.toISOString().substring(0, 10)
          : undefined,
      TypeFilter:
        selectedFilter && selectedFilter.id == "contratationDate"
          ? "start"
          : selectedFilter && selectedFilter.id == "terminationDate"
          ? "end"
          : undefined,
    });
  }, [selectedSituation, startDate, endDate, refresh, selectedFilter]);

  const cleanFileterHandler = useCallback(() => {
    setFilterParams(undefined);
    setStartDate(null);
    setEndDate(null);
    setSelectedSituation(selectedSituationDefault);
  }, [selectedSituationDefault]);

  useEffect(() => {
    fetchSituations();
  }, [fetchSituations]);

  const filters = useMemo(() => {
    return filterParams ? { ...filterParams, refresh } : { refresh };
  }, [filterParams, refresh]);

  const hasPartialDateRange =
    (!!startDate && !endDate) || (!startDate && !!endDate);
  const hasInvalidDateRange =
    !!startDate && !!endDate && startDate > endDate;

  const disabled = useMemo(
    () => !hasFilter || hasPartialDateRange || hasInvalidDateRange,
    [hasFilter, hasPartialDateRange, hasInvalidDateRange]
  );
  const showActions = useMemo(
    () =>
      permissionReactivateContract ||
      permissionCancelContract ||
      permissionRescendContract ||
      permissionApproveContract ||
      deleteContract,
    [
      permissionReactivateContract,
      permissionCancelContract,
      deleteContract,
      permissionRescendContract,
      permissionApproveContract,
    ]
  );
  return (
    <Table<any>
      showDefaultMacroFilters={false}
      service={service}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={filters}
      macroFilters={() => (
        <ModalFilters
          onFilter={filterHandler}
          actionsDisabled={disabled}
          badgeCount={
            filterParams
              ? Object.keys(filterParams).reduce((count, key) => {
                  if (key !== "Refresh" && filterParams[key] !== undefined) {
                    return count + 1;
                  }
                  return count;
                }, 0) -
                (filterParams["InitialDate"] && filterParams["EndDate"] ? 1 : 0)
              : 0
          }
          onCleanFilter={cleanFileterHandler}
        >
          <>
            <AutoCompleteMultiUncontrolled
              label={t(translationPath + "situation") || "Situation"}
              multiple={true}
              onValueSelected={handleSituationSelected}
              value={selectedSituation}
              options={situations}
              size="small"
              fullWidth
            />{" "}
            <DateRangeSelect
              setEndDate={setEndDate}
              setStartDate={setStartDate}
              setEnableFields={setEnableFields}
              showFilter={true}
              filterOptions={filterOptions}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
            />{" "}
            <Box display="flex" alignItems="center" gap={1}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
              <Typography>-</Typography>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            {/* <DateRangeUncontrolled
              onChangeEndDate={setEndDate}
              onChangeStartDate={setStartDate}
              defaultStartDate={startDate}
              defaultEndDate={endDate}
            /> */}
            <Typography variant="caption" color="error" sx={{ mt: 0, mb: 0 }}>
              {hasPartialDateRange
                ? t(translationPath + "filter")
                : hasInvalidDateRange
                ? t(translationPath + "dateFilter")
                : null}
            </Typography>
          </>
        </ModalFilters>
      )}
      searchInputPlaceHolder={t(translationPath + "search")}
      addButtonLabel={t(translationPath + "add")!}
      showDeleteButton={deleteContract}
      defaultActions={showActions}
      rowActions={(params) => [
        permissionApproveContract &&
        params.row.situation.code === "1" &&
        params.row.approvalDate == null
          ? {
              action: () => onApproveSalesContract(params.row.id),
              icon: <CheckCircleIcon fontSize="small" />,
              text: t(translationPath + "approve"),
            }
          : null,
        permissionCancelContract && params.row.cancelationDate === null
          ? {
              action: () => onCancelSalesContract(params.row.id),
              icon: <CancelIcon fontSize="small" />,
              text: t(translationPath + "cancel"),
            }
          : null,

        permissionReactivateContract &&
        ((params.row.situation.code === "4" &&
          params.row.terminationDate != null) ||
          (params.row.situation.code === "3" &&
            params.row.cancelationDate != null))
          ? {
              action: () => onReactivationSalesContract(params.row.id),
              icon: <PlayCircleFilledIcon fontSize="small" />,
              text: t(translationPath + "reactivation"),
            }
          : null,

        permissionRescendContract && params.row.terminationDate === null
          ? {
              action: () => onRescendSalesContract(params.row.id),
              icon: <PauseCircleFilledIcon fontSize="small" />,
              text: t(translationPath + "terminate"),
            }
          : null,
      ]}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default AdvantagesAndBenefitsClubPage;
